import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Ship, Save, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const input = "w-full rounded-sm bg-navy border border-gold/20 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold";
const blank = { ship_name: "", arrival_date: "", arrival_time: "", departure_time: "", dock: "", notes: "" };

export function CruiseScheduleManager() {
  const [ships, setShips] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/cruise-ships"); setShips(data); }
    catch { setShips([]); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => { setForm(blank); setEditId(null); };

  const save = async () => {
    if (!form.ship_name.trim()) return toast.error("Enter a ship name.");
    setSaving(true);
    try {
      if (editId) { await api.put(`/admin/cruise-ships/${editId}`, form); toast.success("Ship updated."); }
      else { await api.post("/admin/cruise-ships", form); toast.success("Ship added."); }
      reset(); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed."); }
    finally { setSaving(false); }
  };

  const edit = (s) => { setEditId(s.id); setForm({ ship_name: s.ship_name, arrival_date: s.arrival_date || "", arrival_time: s.arrival_time || "", departure_time: s.departure_time || "", dock: s.dock || "", notes: s.notes || "" }); };

  const del = async (id) => {
    if (!window.confirm("Remove this ship from the schedule?")) return;
    try { await api.delete(`/admin/cruise-ships/${id}`); setShips((p) => p.filter((x) => x.id !== id)); toast.success("Removed."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed."); }
  };

  return (
    <div className="rounded-sm bg-navy-light border border-gold/20 overflow-hidden" data-testid="cruise-panel">
      <div className="px-6 py-4 border-b border-gold/20 flex items-center gap-2">
        <Ship className="h-5 w-5 text-gold" />
        <h2 className="font-heading text-xl font-bold text-white">Cruise schedule — Halifax</h2>
      </div>

      <div className="p-5 border-b border-gold/10 bg-navy/40">
        <div className="grid gap-2 sm:grid-cols-6">
          <input data-testid="cruise-ship-name" value={form.ship_name} onChange={set("ship_name")} placeholder="Ship name" className={`${input} sm:col-span-2`} />
          <input data-testid="cruise-arrival-date" type="date" value={form.arrival_date} onChange={set("arrival_date")} className={input} />
          <input data-testid="cruise-arrival-time" value={form.arrival_time} onChange={set("arrival_time")} placeholder="Arr. time" className={input} />
          <input data-testid="cruise-departure-time" value={form.departure_time} onChange={set("departure_time")} placeholder="Dep. time" className={input} />
          <input data-testid="cruise-dock" value={form.dock} onChange={set("dock")} placeholder="Dock / pier" className={input} />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button onClick={save} disabled={saving} data-testid="cruise-save" className="inline-flex items-center gap-1.5 rounded-full bg-gold px-5 py-2 text-sm font-semibold text-navy hover:bg-gold-light disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {editId ? "Update ship" : "Add ship"}
          </button>
          {editId && <button onClick={reset} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"><X className="h-4 w-4" /> Cancel</button>}
        </div>
      </div>

      {ships === null ? (
        <div className="p-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : ships.length === 0 ? (
        <div className="p-12 text-center text-slate-400" data-testid="no-ships">No ships scheduled yet. Add one above.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-400 border-b border-gold/20">
              <tr><th className="px-5 py-3">Date</th><th className="px-5 py-3">Ship</th><th className="px-5 py-3">Arrival</th><th className="px-5 py-3">Departure</th><th className="px-5 py-3">Dock</th><th className="px-5 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {ships.map((s) => (
                <tr key={s.id} className="border-b border-gold/5 hover:bg-navy/40" data-testid={`cruise-row-${s.id}`}>
                  <td className="px-5 py-3 text-slate-300 whitespace-nowrap">{s.arrival_date || "—"}</td>
                  <td className="px-5 py-3 text-white font-medium">{s.ship_name}</td>
                  <td className="px-5 py-3 text-slate-400">{s.arrival_time || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{s.departure_time || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{s.dock || "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-3">
                      <button onClick={() => edit(s)} data-testid={`cruise-edit-${s.id}`} className="text-[11px] font-semibold text-sky-300 hover:text-sky-200">Edit</button>
                      <button onClick={() => del(s.id)} data-testid={`cruise-delete-${s.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-300"><Trash2 className="h-3 w-3" /> Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
